import React, {createRef, memo, useContext, useEffect, useState} from 'react';
import { useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {Checkbox, Icon, MenuItem, Select, useTheme} from "@material-ui/core";
import {Email, SaveAlt} from "@material-ui/icons";

import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import {
  defaultComponentsScroll,
  FullWidthFilter,
  MaterialTableBody
} from '../../../controls/Material-Table-Components';
import {ColumnWidth, renderBooleanFilter,} from "../../../blocks/ColumnHelper";
import {HandshakeIcon} from "../../../icons";
import {ComboSelect, DatePicker} from "../../../controls";
import IconButtonMenu from "../../../controls/IconButtonMenu";
import useDimensions from "../../../blocks/useDimensions";
import { useHandoverItems } from "../../../services/flights";
import HandoverDialog from "./HandoverDialog";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";


const HandoverList = (_props) => {
  const {isAuthenticated, user} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const classes = props.classes;
  const colours = SystemData.colours;

  const tableRef = createRef({state: {lastEditingRow: false}});
  const month = props.month;
  const year = props.year;
  const date = props.date || null;
  const shift = props.shift || null

  const queryClient = useQueryClient();
  const extraKeys = date && shift ? ["shift", date, shift] : [year, month];

  const handoverItems = useHandoverItems({tableRef, queryClient, extraKeys});
  const [state] = useState({
    canEdit: props.canEdit,
    canEditFuelLogs: props.canEditFuelLogs,
    year: props.year,
    month: props.month
  });

  const [defaultValues, setDefaultValues] = useState(null);


  let selectedRow = -1;

  const listActionOptions = [
    {action: "Download Handover List", itemLabel: "Download Handover List", itemIcon: <HandshakeIcon/>, historySuffix: " - Downloaded"},
    {action: "Email Handover List", itemLabel: "Email Handover List", itemIcon: <Email/>, historySuffix: " - Emailed"},
  ];

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/tripsheet/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Trip sheet Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Trips' Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Trips sheet Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server is not responding`);
    }
  }

  const refreshList = async () => {
    queryClient.invalidateQueries(["HandoverItems", ...extraKeys]);
  }

  const addOrUpdateItem = async (rowData) => {
    if (rowData)
      rowData["delayedUntil"] = rowData.targetCompletion;
    const title = `${rowData ? `Update` : `Add New`} Handover Item`;
    const now = moment();
    const record = {
      date: now.format("YYYY-MM-DD"),
      shift: now.isBefore(moment.utc("08:00:00", "HH:mm:ss")) || now.isAfter(moment.utc("17:00:00", "HH:mm:ss")) ? "NIGHT" : "DAY",
      category: "",
      priority: "",
      detail: "",
      targetCompletion: moment().format("YYYY-MM-DD"),
      delayedUntil: "",
      completed: false,
      additionalData: "",
    }
    eventBus.trigger('HandoverItemDialog.open', {title, canEdit: state.canEdit, record: rowData || record});
  }


  async function executeListAction(params) {
    if (params) {
      switch (params.selectedOption) {
        case "Download Handover List":
          downloadFlightSchedule("airline", "CC", true);
          break;
        case "Email Handover List":
          downloadFlightSchedule("airline", "CC", false);
          break;
        default:
          break;
      }
    }
  }

  async function downloadFlightSchedule(target, filter, download = true, companyUuid = null, companyName = "") {

    if (!target)
      return;

    const startDateMoment = moment(`${props.year}-${props.month}-01`);
    const startDate = startDateMoment.isValid() ? startDateMoment.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const endDate = moment(startDate).endOf("month").format("YYYY-MM-DD");
    const route = target === "delayReport" ? "delayReport" : target === "hoursFuelReport" ? "hoursFuelReport" : target === "airline" ? "lessorschedule" : target==="customer"?"customerschedule":"airportschedule";

    let url = `${apiSitePrefix}/trip/${route}/${startDate}/${endDate}/${filter}`;

    if (url) {
      try {
        SystemData.toggleLoadingProgress(true, `Preparing Flights Report ...`);
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          SystemData.toggleLoadingProgress(false);
          if (response.data.status) {
            if (!download) {
              let templateType;
              let subject;
              let attachmentDescription;
              const displayDateFormat = "DD MMM YYYY";
              const scheduleFrom = startDateMoment.isValid() ? startDateMoment.format(displayDateFormat) : moment().format(displayDateFormat);
              const scheduleTo = moment(scheduleFrom, displayDateFormat).endOf("month").format(displayDateFormat);
              const fileName = response.data.downloadFileName;
              switch (target) {
                case "airline":
                  subject = `Magma Aviation Flight Schedule // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "OPERATOR_FLIGHTS";
                  attachmentDescription = `Magma Aviation flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                case "customer":
                  subject = `Magma Aviation Flight Schedule // ${companyName} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "CUSTOMER_FLIGHTS";
                  attachmentDescription = `Magma Aviation flight schedule for ${companyName} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                default:
                  subject = `Magma Aviation Flight Schedule // ${filter} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "AIRPORT_FLIGHTS";
                  attachmentDescription = `Magma Aviation flight schedule via ${filter} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
              }

              const emailRecord = {
                target: target,
                targetFilter: target === "airport" && companyUuid ? companyUuid : filter,
                recipients: "",
                carbonCopy: "",
                subject: subject,
                templateType: templateType,
                templateUuid: "dummyTemplateUuid",
                attachmentDescription: attachmentDescription,
                attachmentUrl: response.data.excelSignedUrl,
                fileName: fileName.replaceAll("/",""),
                repeatData: [],
                contactRole: "FLIGHTS",
                airportCode: target === "airport"?filter:undefined
              }
              SystemData.sendEmail(emailRecord, `Send Flight Schedule`, true);

              return;
            }

            const fileName = response.data.downloadFileName;
            let a = document.createElement('a');
            a.href = response.data.excelSignedUrl;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); }, 0);
          } else {
            SystemData.openAlert(
              "Download Error",
              `The report could not be downloaded due to the following error: ${response.data.error}`
            );
          }
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.openAlert(
          "Download Error",
          `The Schedule report could not be downloaded due to the following unexpected error: ${err}`
        );
      }
    }
  }


  const handleRowClick = (event, rowData) => {
    if (rowData.tableData.id >= 0) {
      selectedRow = rowData.tableData.id;
      addOrUpdateItem(rowData);
    }
  }


  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize: 12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      filterCellStyle: { fontSize: "0.7rem", padding: "4px 2px" },
      editable: "never",
      sorting: false,
      resizable: false,
      emptyValue: "",
      initialEditValue: "",
    };

    const cellStyle = { fontSize: 12, padding: "4px 8px" };
    const borderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" };
    const invisibleBorderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const boldInvisibleBorderedCellStyle = { fontSize: 13, fontWeight: 800, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const borderedHeaderStyle = { borderLeft: "0px solid rgb(63, 63, 63)", borderRight: "1px solid rgb(63, 63, 63)" };
    const headerStyle = { fontSize: 12, padding: "4px 6px" };
    const filterStyle = { fontSize: 12, padding: "9px 2px", marginLeft: -12 };
    // const borderedFilterStyle = {}; //{ borderLeft: "0px solid #ffffff", borderRight: "1px solid #ffffff" };

    return [
      //{...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, cellStyle, headerStyle, undefined, false),
        title: "Date",
        field: "date",
        render: rowData => {
          const flightDate = moment(rowData.date, "YYYY-MM-DD HH:mm:ss");
          return ( <span>{flightDate.format("DD MMM")}</span>);
        },
        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, undefined, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Shift",
        field: "shift",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => {
          const comboValues = [{"code":"DAY","value":"Day"}, {"code":"NIGHT","value":"Night"}];
          return (
            <Select
              style={{width: "100%"}}
              value={props.value == null?'':props.value}
              onChange={
                (evt, data) => {
                  props.onChange(data.props.value);
                }
              }
              renderValue={(value) => `${value}`}
            >
              {(comboValues) ?
                comboValues.map((record) => {
                  return (
                    <MenuItem key={record.code} value={record.value}>{record.value}</MenuItem>
                  );
                }) :
                null
              }
            </Select>
          )
        },
        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(140,undefined,undefined,cellStyle,headerStyle,filterStyle,false),
        title: "Category",
        field: "category",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        fullWidth: true,
        sorting: true,
        render: rowData => (
          <ComboSelect
            group="HANDOVER CATEGORIES"
            subGroup=""
            value={rowData.category}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
            <ComboSelect
              group="HANDOVER CATEGORIES"
              subGroup=""
              // fullWidth
              value={props.value}
              onChange={props.onChange}
            />
          )
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(140,undefined,undefined,cellStyle,headerStyle,filterStyle,false),
        title: "Priority",
        field: "priority",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        fullWidth: true,
        sorting: true,
        render: rowData => (
          <ComboSelect
            group="HANDOVER PRIORITIES"
            subGroup=""
            value={rowData.priority}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
            <ComboSelect
              group="HANDOVER PRIORITIES"
              subGroup=""
              // fullWidth
              value={props.value}
              onChange={props.onChange}
            />
          )
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(150,undefined,undefined,cellStyle,headerStyle,filterStyle,false),
        title: "Target Completion",
        field: "targetCompletion",
        sorting: false,
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            // initialValue={minDate}
            // minDate={props.value || minDate}
            // defaultValue={initialDate}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <div>{moment.utc(rowData.targetCompletion).format("DD/MM/YYYY HH:mm")}</div>
        ),
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(900,undefined,undefined,cellStyle,headerStyle,filterStyle,false),
        title: "Details",
        field: "detail",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(10,undefined,undefined,undefined,headerStyle,filterStyle,false),
        title: "",
        field: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,undefined,undefined,undefined,headerStyle,filterStyle,false),
        title: "Done",
        field: "completed",
        emptyValue: false,
        initialEditValue: false,
        type: "boolean",
        defaultFilter: undefined, // 'checked', 'unchecked', or undefined
        filterComponent: (props) => renderBooleanFilter({...props, size: "medium"}),
        render: rowData =>
          <Checkbox
            checked={(rowData.completed === 1 || rowData.completed === true) ?? true}
          />,
        editComponent: props =>
          <Checkbox
            checked={(props.rowData.completed === 1 || props.rowData.completed === true) ?? true}
            onChange={ (event, checked) => {
              props.onChange(checked)
            }}
          />,
      },
      {
        ...baseColumn,
        ...ColumnWidth(150,undefined,undefined,undefined,headerStyle,filterStyle,false),
        title: "Completed By",
        field: "completedBy",
        defaultFilter: undefined, // 'checked', 'unchecked', or undefined
      },
      {
        ...baseColumn,
        title: "",
        field: "",
      },
     ];
  };

  const ItemRows = (props) => {
    const theader = tableContainerRef.current?.querySelector('thead');
    const thead = theader ? theader.clientHeight:30;
    const bodyHeight = tableHeight - thead;

    return (
      <MaterialTable
        icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
        tableRef={tableRef}
        style={{
          border: date && shift ? '1px solid #d3d4d5' : 'none',
          boxShadow: "none",
          borderRadius: 0,
        }}
        onRowClick={handleRowClick}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "4px 2px",
          },
          filterCellStyle: {
            padding: "4px 2px",
            fontSize: "0.7rem",
          },
          headerRowStyle: {
            display: "table",
            width: "100%",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "4px 2px",
          },
          filterRowStyle: {
            paddingLeft: state.canEdit ? 0 : 0,
            width: state.canEdit ? "calc(100% - 15px)" : "100%",
          },
          highlightRowStyle: {backgroundColor: "#F69259"},
          rowStyle: (rowData)=> ({opacity: rowData.cancelled?0.5:1, borderBottom: `${rowData.dateChange?"3":"1"}px solid rgba(224, 224, 224, 1)`}),
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          paging: false,
          filtering: true,
          search: false,
          toolbar: true,
          title: "Handover List"
        }}
        components={{
          ...defaultComponentsScroll,
          Body: (props) => (
            <MaterialTableBody
              {...props}
              rowHeight={46}
              overscanRowCount={20}
              addVirtualScrolling={true}
              bodyHeight={bodyHeight}
              headerHeight={thead}
              filterRowHeight={46}
            />
          )
        }}
        localization={{
          header: {
            actions: `Actions${String.fromCharCode(160)}`
          },
        }}
        actions={[
          {
            hidden: false,
            disabled: false,
            icon: () => {
              return(
                <IconButtonMenu
                  icon={<SaveAlt/>}
                  size={"small"}
                  offset={-1}
                  style={{ color: theme.palette.primary.main }}
                  tooltip={"Handover List OPtions"}
                  menuItems={listActionOptions}
                  callback={executeListAction}
                  callbackParams={{}}
                />)
            },
            isFreeAction: true,
          },
          date && shift ? null :
          {
            icon: 'add_box',
            tooltip: "Add Handover Item",
            isFreeAction: true,
            onClick: (e) => {
              addOrUpdateItem();
            }
          },
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
        ]}
        title={`Handover List ${date && shift ? `for the ${shift} shift on ${moment(date).format(dateFormat)}` : ``}`}
        columns={getColumns({canEdit: state.canEdit})}
        data={props.data}
      />
    )
  };

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});
  if (!defaultValues || __.isEmpty(defaultValues))
    getDefaultValues();

  return (isAuthenticated &&
      <div ref={tableContainerRef}  style={{width: "100%", marginLeft: 20, noWidth: "calc(100% - 24px)"}}>
        <ItemRows data={handoverItems.data}/>
        <HandoverDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          classes={classes}
          // afterSave={()=>{eventBus.trigger("RouteView.refreshLeg")}}
          // onClose={()=>eventBus.trigger('RouteView.pause', false)}
        />
      </div>
  );

}

export default HandoverList;
